import React, { useState, useEffect } from 'react';
import {
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
  ColumnInstance,
  Row,
  IdType
} from 'react-table';
import { useAuth } from 'services/auth-service';
import { HelpTooltipIcon } from 'components/elements/Help/HelpTooltipIcon';
import ExportIcon from 'assets/Icons/export/ExportIcon';
import ImportIcon from 'assets/Icons/import/ImportIcon';
import CreateIcon from 'assets/Icons/modals/CreateIcon';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import TertiaryButton from 'components/elements/Form/button/tertiaryButton';
import TopBar from 'components/elements/TopBar';
import EditNotificationsIcon from '@mui/icons-material/EditNotifications';
import Box from '@mui/material/Box';
import { Grid, IconButton, Tooltip, useMediaQuery, useTheme, TableSortLabel } from '@mui/material';
import './Table.scss';
import { colours } from 'utils/constants';
import NoData from 'components/elements/Reports/NoData';
import Pagination from './Pagination';
import FullScreenLoader from 'components/elements/Spinner/FullScreenLoader';

/**
 * There's some boilter plate here
 * Important part is useTable function
 * we add the useGlobalFilter hook
 * then we also add an input which has
 * onChange={handleFilterInputChange}
 * */

const caseInsensitiveSort = <T extends object>(
  rowA: Row<T>,
  rowB: Row<T>,
  columnId: IdType<T>
): number => {
  const a = rowA?.values[columnId];
  const b = rowB?.values[columnId];
  if (isNaN(a)) {
    return a?.toString().toUpperCase() > b?.toString().toUpperCase() ? 1 : -1;
  }
  return Number(a) > Number(b) ? 1 : -1;
};

export interface TableProps<T extends object> {
  columns: any;
  data: T[];
  name?: string;
  defaultSortBy?: { id: string; desc: boolean };
  CreateForm?: any;
  DeleteForm?: any;
  ImportForm?: any;
  ExportForm?: any;
  SpaceAlertsForm?: any;
  refetch?: any;
  createQuery?: any;
  updateQuery?: any;
  deleteQuery?: any;
  editQuery?: any;
  importQuery?: any;
  queryName: string;
  buttonName?: string;
  toolTipMessage?: string;
  isLoading?: boolean;
  gotoPageZero?: number;
  topElement?: JSX.Element;
}

export function Table<T extends object>({
  columns,
  data,
  name,
  defaultSortBy,
  CreateForm,
  refetch,
  DeleteForm,
  ImportForm,
  ExportForm,
  SpaceAlertsForm,
  createQuery,
  updateQuery,
  deleteQuery,
  editQuery,
  importQuery,
  queryName,
  buttonName = 'Item',
  toolTipMessage,
  isLoading = false,
  gotoPageZero = 0,
  topElement
}: TableProps<T>): React.ReactElement {
  const [createShowModal, setCreateShowModal] = useState(false);
  const [updateShowModal, setUpdateShowModal] = useState(false);
  const [deleteShowModal, setDeleteShowModal] = useState(false);
  const [importShowModal, setImportShowModal] = useState(false);
  const [visitorExportShowModal, setVisitorExportShowModal] = useState(false);
  const [occupantExportShowModal, setOccupantExportShowModal] = useState(false);
  const [spaceAlertsShowModal, setSpaceAlertsShowModal] = useState(false);
  const [editUUID, setEditUUID] = useState('');
  const [deleteUUID, setDeleteUUID] = useState('');
  const [resourceUUID, setResourceUUID] = useState('');
  const {
    state: { user }
  } = useAuth();

  const defaultColumn = {
    disableSortBy: true
  };

  const {
    getTableProps,
    getTableBodyProps,
    columns: finalColumns,
    headerGroups,
    prepareRow,
    page, // Instead of using 'rows', we'll use page,
    // which has only the rows for the active page

    // The rest of these things are super handy, too ;)
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter, filters },
    toggleSortBy
  } = useTable<T>(
    {
      columns,
      data,
      defaultColumn,
      initialState: {
        pageIndex: 0,
        sortBy: defaultSortBy ? [defaultSortBy] : [] // Apply default sort column if provided
      },
      setEditUUID,
      setDeleteUUID,
      setResourceUUID,
      setUpdateShowModal,
      setDeleteShowModal,
      autoResetFilters: false,
      autoResetGlobalFilter: false,
      autoResetPage: false,
      autoResetSortBy: false,
      sortTypes: {
        alphanumeric: caseInsensitiveSort
      }
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination
  );

  // Reset page index to 0 when global filter or column filters change
  useEffect(() => {
    gotoPage(0);
  }, [globalFilter, filters, gotoPage]);

  useEffect(() => {
    gotoPage(0);
  }, [gotoPageZero, gotoPage]);

  // Refetch the data once an item is created, updated or deleted
  useEffect(() => {
    if (!createShowModal || !updateShowModal || !deleteShowModal) {
      refetch();
    }
  }, [createShowModal, deleteShowModal, refetch, updateShowModal]);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Sort toggle handler
  const handleSortToggle = (column: ColumnInstance<T>) => {
    if (!column.canSort) return;
    const isSorted = column.isSorted;
    const isSortedDesc = column.isSortedDesc;

    if (!isSorted) {
      toggleSortBy(column.id, false, false); // Sort ascending
    } else if (isSorted && !isSortedDesc) {
      toggleSortBy(column.id, true, false); // Sort descending
    } else {
      toggleSortBy(column.id, false, false); // Reset to ascending
    }
  };

  let content: JSX.Element;

  if (isLoading) {
    content = (
      <Box sx={{ marginTop: '50px', width: '100%' }}>
        <FullScreenLoader />
      </Box>
    );
  } else {
    content = (
      <Box
        sx={{
          overflowX: 'auto',
          '& .table': { minWidth: 600 }
        }}
      >
        <div className="table">
          {!data || !data.length ? (
            <NoData
              height={300}
              hint={`No ${(buttonName ?? '').toLowerCase()} records available.`}
            />
          ) : (
            <table {...getTableProps()}>
              <thead className="table-header">
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps()}>
                        {column.canSort ? (
                          <TableSortLabel
                            active={column.isSorted}
                            direction={column.isSortedDesc ? 'desc' : 'asc'}
                            onClick={() => handleSortToggle(column)}
                          >
                            {column.render('Header')}
                          </TableSortLabel>
                        ) : (
                          column.render('Header')
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="table-contents" {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          <div>
            {data.length > 0 && (
              <Pagination
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageCount={pageCount}
                pageIndex={pageIndex}
                pageOptions={pageOptions}
                pageSize={pageSize}
                setPageSize={setPageSize}
                gotoPage={gotoPage}
                nextPage={nextPage}
                previousPage={previousPage}
              />
            )}
          </div>
        </div>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <TopBar setGlobalFilter={setGlobalFilter} isMessage={true} />
      <div className="flex items-center space-x-2 pb-9">
        <h1 className="pb-0">{name}</h1>
        {toolTipMessage && (
          <div className="pb-2">{<HelpTooltipIcon message={toolTipMessage} />}</div>
        )}
      </div>
      {occupantExportShowModal && (
        <ExportForm
          entityName={`Export CSV`}
          showModal={occupantExportShowModal}
          setShowModal={setOccupantExportShowModal}
        />
      )}
      {importShowModal && (
        <ImportForm
          entityName={`Import CSV`}
          showModal={importShowModal}
          setShowModal={setImportShowModal}
          importQuery={importQuery}
          queryName={queryName}
        />
      )}
      {visitorExportShowModal && (
        <ExportForm
          entityName={`Export CSV`}
          showModal={visitorExportShowModal}
          setShowModal={setVisitorExportShowModal}
        />
      )}
      {spaceAlertsShowModal && (
        <SpaceAlertsForm
          entityName={`Manage Space Alerts`}
          showModal={spaceAlertsShowModal}
          setShowModal={setSpaceAlertsShowModal}
        />
      )}
      {createShowModal && (
        <CreateForm
          entityName={`Add ${buttonName}`}
          showModal={createShowModal}
          setShowModal={setCreateShowModal}
          createQuery={createQuery}
          queryName={queryName}
        />
      )}
      {updateShowModal && (
        <CreateForm
          entityName={`Update ${buttonName}`}
          showModal={updateShowModal}
          setShowModal={setUpdateShowModal}
          setEditUUID={setEditUUID}
          editQuery={editQuery}
          editUUID={editUUID}
          updateQuery={updateQuery}
          queryName={queryName}
        />
      )}
      {deleteShowModal && (
        <DeleteForm
          showModal={deleteShowModal}
          setShowModal={setDeleteShowModal}
          setDeleteUUID={setDeleteUUID}
          setResourceUUID={setResourceUUID}
          deleteUUID={deleteUUID}
          resourceUUID={resourceUUID}
          editQuery={editQuery}
          deleteQuery={deleteQuery}
          queryName={queryName}
          entityName={buttonName}
        />
      )}
      {/* Filters and page buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' }
        }}
      >
        <Grid
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: { xs: 'wrap', sm: 'wrap', md: 'wrap', lg: 'nowrap' }
          }}
        >
          <Grid item>{topElement}</Grid>
          {finalColumns
            .filter((column) => column.isVisible)
            .map((column) => {
              return column.canFilter ? (
                <Grid key={column.id} sx={{ flexGrow: 1, minWidth: 100 }}>
                  {column.render('Filter')}
                </Grid>
              ) : null;
            })}
        </Grid>
        {
          <Grid
            container
            justifyContent={{ xs: 'flex-end' }}
            spacing={2}
            paddingTop={{ xs: '15px', md: '0' }}
          >
            {queryName === 'occupants' &&
              (user.role === 'SUPER' || user.role === 'ACCOUNT' || user.role === 'BUILDING') && (
                <Grid item>
                  {isSmallScreen ? (
                    <Tooltip title="Export CSV">
                      <IconButton
                        onClick={() => setOccupantExportShowModal(true)}
                        size="large"
                        sx={{
                          backgroundColor: '#FFFFFF',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '8px'
                        }}
                      >
                        <ExportIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <TertiaryButton
                      onClick={() => setOccupantExportShowModal(true)}
                      buttonLabel={`Export CSV`}
                      iconLeft={<ExportIcon />}
                      position="bg-primary2"
                    />
                  )}
                </Grid>
              )}
            {queryName === 'occupants' &&
              (user.role === 'SUPER' ||
                user.role === 'ACCOUNT' ||
                user.role === 'BUILDING' ||
                user.role === 'ORG') && (
                <Grid item>
                  {isSmallScreen ? (
                    <Tooltip title="Import CSV">
                      <IconButton
                        onClick={() => setImportShowModal(true)}
                        size="large"
                        sx={{
                          backgroundColor: '#FFFFFF',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '8px'
                        }}
                      >
                        <ImportIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <TertiaryButton
                      onClick={() => setImportShowModal(true)}
                      buttonLabel={`Import CSV`}
                      iconLeft={<ImportIcon />}
                      position="bg-primary2"
                    />
                  )}
                </Grid>
              )}
            {queryName === 'visitors' &&
              (user.role === 'SUPER' || user.role === 'ACCOUNT' || user.role === 'BUILDING') && (
                <Grid item>
                  {isSmallScreen ? (
                    <Tooltip title="Export CSV">
                      <IconButton
                        onClick={() => setVisitorExportShowModal(true)}
                        size="large"
                        sx={{
                          backgroundColor: '#FFFFFF',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '8px'
                        }}
                      >
                        <ExportIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <TertiaryButton
                      onClick={() => setVisitorExportShowModal(true)}
                      buttonLabel={`Export CSV`}
                      iconLeft={<ExportIcon />}
                      position="bg-primary2"
                    />
                  )}
                </Grid>
              )}
            {queryName === 'spaces' &&
              (user.role === 'SUPER' || user.role === 'ACCOUNT' || user.role === 'BUILDING') && (
                <Grid item>
                  {isSmallScreen ? (
                    <Tooltip title="Manage Alerts">
                      <IconButton
                        onClick={() => setSpaceAlertsShowModal(true)}
                        size="large"
                        sx={{
                          backgroundColor: '#FFFFFF',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '8px'
                        }}
                      >
                        <EditNotificationsIcon sx={{ color: colours.seafoam }} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <TertiaryButton
                      onClick={() => setSpaceAlertsShowModal(true)}
                      buttonLabel={`Manage Alerts`}
                      position="bg-primary2 text-primary1 border border-primary1"
                    />
                  )}
                </Grid>
              )}
            {(queryName !== 'spaces' || user.role === 'SUPER') && user.role !== 'ORG' && (
              <Grid item>
                <PrimaryButton
                  position="mr-4"
                  onClick={() => setCreateShowModal(true)}
                  variant="default"
                  size="px-4 w-auto gap-2 justify-end md:py-4"
                  buttonLabel={`Create ${buttonName}`}
                  iconLeft={<CreateIcon />}
                />
              </Grid>
            )}
          </Grid>
        }
      </Box>

      {/* Table */}
      {content}
    </Box>
  );
}
